import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, RotateCcw, Trash2, X, ImageOff } from 'lucide-react';
import { isZoomAtDefault } from '@/utils/zoom';
import { MarkerActionsPopup } from '@/features/shared/MarkerActionsPopup';

const DEFAULT_MARKER_COLOR = '#39FF14';

export const newLiteId = () => `lite-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getSFTypeImageUrl = (sfType, apiBase) => {
  if (!sfType) return null;
  return (
    sfType.drawingCloudinary?.urls?.large ||
    sfType.drawingCloudinary?.urls?.medium ||
    (sfType.drawingUrl ? `${apiBase}${sfType.drawingUrl}` : null)
  );
};

// Window and Door types both store their picture in pictureCloudinary / pictureUrl
export const getTypePictureUrl = (type, apiBase) => {
  if (!type) return null;
  return (
    type.pictureCloudinary?.urls?.large ||
    type.pictureCloudinary?.urls?.medium ||
    (type.pictureUrl ? `${apiBase}${type.pictureUrl}` : null)
  );
};

const liteLabel = (lites, liteId) => {
  const index = lites.findIndex(l => l.id === liteId);
  return index === -1 ? '?' : `L${index + 1}`;
};

// Shared Type image marking for a schedule's lites (Storefront Schedule, Window Schedule).
// Interaction mirrors the Elevations marker implementation: percentage coordinates,
// scale/translate zoom + pan, draggable square markers with a delete popup.
export const TypeImageMarkingSection = ({
  type,
  imageUrl,
  lites = [],
  marks = [],
  onChangeMarks,
  canEdit = true,
  title = 'Type Image Marking',
  typeLabel = 'Type',
  testIdPrefix = 'type-mark',
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [activePopup, setActivePopup] = useState(null);
  const [selectedLiteId, setSelectedLiteId] = useState(null);
  const panStart = useRef({ x: 0, y: 0 });
  const dragState = useRef(null);

  const markerColor = type?.markerColor || DEFAULT_MARKER_COLOR;
  const activeLiteId = lites.some(l => l.id === selectedLiteId) ? selectedLiteId : lites[0]?.id || null;

  const handleZoomIn = () => setZoom(z => Math.min(3, z + 0.25));
  const handleZoomOut = () => setZoom(z => Math.max(0.5, z - 0.25));
  const handleResetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handlePanStart = (e) => {
    if (zoom <= 1 || dragState.current) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const handlePanMove = (e) => {
    if (!isPanning) return;
    setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  };
  const handlePanEnd = () => setIsPanning(false);

  const handleImageClick = (e) => {
    if (!canEdit || isPanning || !activeLiteId) return;
    setActivePopup(null);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onChangeMarks([
      ...marks,
      { id: `mark-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, liteId: activeLiteId, x, y },
    ]);
  };

  const handleMarkMouseDown = (e, mark) => {
    if (!canEdit) return;
    e.stopPropagation();
    dragState.current = { id: mark.id, startX: e.clientX, startY: e.clientY, moved: false };
  };

  const handleMarkMouseMove = (e) => {
    const drag = dragState.current;
    if (!drag) return;
    const distance = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY);
    if (distance <= 5 && !drag.moved) return;
    drag.moved = true;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    onChangeMarks(marks.map(m => (m.id === drag.id ? { ...m, x, y } : m)));
  };

  const handleMarkMouseUp = () => {
    if (dragState.current) {
      const { moved } = dragState.current;
      dragState.current = null;
      if (moved) return;
    }
    handlePanEnd();
  };

  const handleDeleteMark = (markId) => onChangeMarks(marks.filter(m => m.id !== markId));

  const markCount = (liteId) => marks.filter(m => m.liteId === liteId).length;

  if (!type) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-empty-no-type`}>
        <h3 className="font-semibold text-lg border-b pb-2">{title}</h3>
        <div className="text-center py-8 border rounded-lg bg-slate-50">
          <ImageOff className="h-6 w-6 mx-auto text-slate-400 mb-2" />
          <p className="text-slate-500 text-sm">Select a {typeLabel} in step 1 to mark its image.</p>
        </div>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-empty-no-image`}>
        <h3 className="font-semibold text-lg border-b pb-2">{title}</h3>
        <div className="text-center py-8 border rounded-lg bg-slate-50">
          <ImageOff className="h-6 w-6 mx-auto text-slate-400 mb-2" />
          <p className="text-slate-500 text-sm">
            <span className="font-medium">{type.name}</span> has no picture uploaded yet.
          </p>
          <p className="text-slate-400 text-xs mt-1">Upload a picture on the {typeLabel} to mark lites on it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between border-b pb-2">
        <div>
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-xs text-slate-500 mt-1">
            {lites.length === 0
              ? 'Add a lite above to start marking it on the image'
              : 'Select a lite, then click the image to mark it. A lite can have multiple marks.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleZoomOut} disabled={zoom <= 0.5} title="Zoom Out" data-testid={`${testIdPrefix}-zoom-out`}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-slate-600 min-w-[60px] text-center" data-testid={`${testIdPrefix}-zoom-level`}>
            {Math.round(zoom * 100)}%
          </span>
          <Button type="button" variant="outline" size="sm" onClick={handleZoomIn} disabled={zoom >= 3} title="Zoom In" data-testid={`${testIdPrefix}-zoom-in`}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            type="button" variant="outline" size="sm" onClick={handleResetZoom}
            disabled={isZoomAtDefault(zoom) && pan.x === 0 && pan.y === 0}
            title="Reset zoom to 100%" aria-label="Reset zoom to 100%" data-testid={`${testIdPrefix}-reset-zoom`}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {lites.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-lite-selector`}>
          {lites.map((lite, index) => {
            const isActive = lite.id === activeLiteId;
            const count = markCount(lite.id);
            return (
              <button
                key={lite.id}
                type="button"
                onClick={() => setSelectedLiteId(lite.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  isActive
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                }`}
                data-testid={`${testIdPrefix}-lite-chip-${index}`}
              >
                Lite {index + 1} · {lite.liteType === 'metalPanel' ? 'Metal' : 'Glass'}
                {count > 0 && (
                  <span className={`ml-1.5 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>({count})</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div
        className="relative overflow-auto border border-slate-200 rounded bg-slate-50"
        style={{
          maxHeight: '45vh',
          cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
        }}
        data-testid={`${testIdPrefix}-image-container`}
      >
        <div
          className="relative inline-block"
          style={{
            transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
            transformOrigin: 'top left',
            transition: isPanning ? 'none' : 'transform 0.2s ease-out',
          }}
          onMouseDown={handlePanStart}
          onMouseMove={(e) => { handlePanMove(e); handleMarkMouseMove(e); }}
          onMouseUp={handleMarkMouseUp}
          onMouseLeave={handleMarkMouseUp}
        >
          <img
            src={imageUrl}
            alt={type.name}
            className="w-full"
            style={{ cursor: canEdit && activeLiteId ? 'crosshair' : 'default' }}
            onClick={handleImageClick}
            draggable={false}
            data-testid={`${testIdPrefix}-image`}
          />

          {marks
            .filter(mark => lites.some(l => l.id === mark.liteId))
            .map((mark) => (
              <div
                key={mark.id}
                className="absolute"
                style={{
                  left: `${mark.x}%`,
                  top: `${mark.y}%`,
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  padding: '8px',
                  zIndex: activePopup === mark.id ? 50 : 10,
                }}
                onMouseDown={(e) => {
                  if (!e.target.closest('.marker-popup')) handleMarkMouseDown(e, mark);
                }}
                data-testid={`${testIdPrefix}-${mark.id}`}
              >
                <div className="flex items-center gap-1">
                  <div
                    data-testid={`${testIdPrefix}-dot-${mark.id}`}
                    style={{
                      width: '12px',
                      height: '12px',
                      border: `2px solid ${markerColor}`,
                      backgroundColor: activePopup === mark.id ? markerColor : `${markerColor}1A`,
                      boxShadow: `0 0 8px ${markerColor}99`,
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = markerColor; }}
                    onMouseLeave={(e) => {
                      if (activePopup !== mark.id) e.currentTarget.style.backgroundColor = `${markerColor}1A`;
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setActivePopup(activePopup === mark.id ? null : mark.id);
                    }}
                  />
                  <span
                    className="text-[10px] font-semibold px-1 rounded bg-slate-900/80 text-white pointer-events-none"
                    data-testid={`${testIdPrefix}-label-${mark.id}`}
                  >
                    {liteLabel(lites, mark.liteId)}
                  </span>
                </div>

                {activePopup === mark.id && (
                  <MarkerActionsPopup zoom={zoom}>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setActivePopup(null); handleDeleteMark(mark.id); }}
                        className="p-1 bg-red-500 hover:bg-red-600 text-white rounded transition-colors flex items-center justify-center"
                        title="Delete mark"
                        style={{ minWidth: '24px', minHeight: '24px' }}
                        data-testid={`${testIdPrefix}-delete-${mark.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setActivePopup(null); }}
                      className="p-1 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors flex items-center justify-center"
                      title="Close"
                      style={{ minWidth: '24px', minHeight: '24px' }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </MarkerActionsPopup>
                )}
              </div>
            ))}
        </div>
      </div>

      <p className="text-xs text-slate-500" data-testid={`${testIdPrefix}-summary`}>
        {marks.length === 0
          ? 'No marks yet'
          : `${marks.length} mark${marks.length === 1 ? '' : 's'} on ${new Set(marks.map(m => m.liteId)).size} of ${lites.length} lite${lites.length === 1 ? '' : 's'}`}
      </p>
    </div>
  );
};

export default TypeImageMarkingSection;
